import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Components
import DiscountBanner from './components/DiscountBanner';
import CookieConsentBanner from './components/CookieConsentBanner';

// Pages
import LandingPage from './pages/LandingPage';
import SignupPage from './pages/SignupPage';
import SigninPage from './pages/SigninPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import CreateAvatarPage from './pages/CreateAvatarPage';
import AvatarChatPage from './pages/AvatarChatPage';
import ProjectChatPage from './pages/ProjectChatPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AddonPaymentSuccessPage from './pages/AddonPaymentSuccessPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import BetaSignupPage from './pages/BetaSignupPage';
import BetaExpiredPage from './pages/BetaExpiredPage';
import RelationCaptureScreen from './pages/RelationCaptureScreen';

function RequireSubscription({ children }) {
  // 'pending' = still verifying, 'ok' = allow through, 'blocked' = redirect
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      // 1. Fast path: sessionStorage already has an active subscription
      try {
        const raw = sessionStorage.getItem('user_data');
        const userData = raw ? JSON.parse(raw) : null;
        if (userData?.subscription?.status === 'ACTIVE') {
          if (!cancelled) setStatus('ok');
          return;
        }
        // Beta user with unexpired beta period — allow through without a subscription
        const isBetaUser = userData?.is_beta === true &&
          userData?.beta_expires_at &&
          new Date(userData.beta_expires_at) > new Date();
        if (isBetaUser) {
          if (!cancelled) setStatus('ok');
          return;
        }
      } catch (_) { /* fall through */ }

      // 2. Gift invitee (inviter already paid) — allow through without API call.
      // Check sessionStorage user_data first (populated by /me on every login),
      // then fall back to localStorage signup_invite_params (set on first invite-link visit).
      try {
        const raw = sessionStorage.getItem('user_data');
        const userData = raw ? JSON.parse(raw) : null;
        if (userData?.invitee?.i_will_pay === 1) {
          if (!cancelled) setStatus('ok');
          return;
        }
      } catch (_) { /* fall through */ }
      try {
        const storedInvite = JSON.parse(localStorage.getItem('signup_invite_params') || '{}');
        if (String(storedInvite.i_will_pay) === '1') {
          if (!cancelled) setStatus('ok');
          return;
        }
      } catch (_) { /* fall through */ }

      // 3. sessionStorage is stale (e.g. right after payment/avatar creation) —
      //    confirm with the API before blocking the user.
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          if (!cancelled) setStatus('blocked');
          return;
        }
        const apiBase = (process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai/api').replace(/\/api$/, '');
        const res = await fetch(`${apiBase}/api/addons/my-plan`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          // Update sessionStorage so subsequent route checks are fast
          try {
            const raw = sessionStorage.getItem('user_data');
            const userData = raw ? JSON.parse(raw) : {};
            const plan = await res.clone().json().catch(() => null);
            if (plan) {
              userData.subscription = { status: 'ACTIVE', ...plan };
              sessionStorage.setItem('user_data', JSON.stringify(userData));
            }
          } catch (_) { /* non-critical */ }
          if (!cancelled) setStatus('ok');
          return;
        }
      } catch (_) { /* fall through */ }

      if (!cancelled) setStatus('blocked');
    };

    verify();
    return () => { cancelled = true; };
  }, []);

  if (status === 'pending') return null;
  if (status === 'blocked') return <Navigate to="/select-plan" replace />;
  return children;
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect invite links to signup EXCEPT when the link intentionally targets /signin.
  // - New users should land on /signup
  // - Existing users can land on /signin (email invite logic decides which one to send)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteId = params.get('invite_id');
    const inviteEmail = params.get('invite_email');
    const isSignup = location.pathname === '/signup';
    const isSignin = location.pathname === '/signin';
    const isCreateAvatar = location.pathname === '/create-avatar';
    if ((inviteId || inviteEmail) && !isSignup && !isSignin && !isCreateAvatar) {
      const newParams = new URLSearchParams();
      if (inviteId) newParams.set('invite_id', inviteId);
      if (inviteEmail) newParams.set('invite_email', inviteEmail);
      navigate(`/signup?${newParams.toString()}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  return (
    <div className="app">
      <DiscountBanner />
      <CookieConsentBanner />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signin" element={<SigninPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/create-avatar" element={<CreateAvatarPage />} />
        <Route path="/chat" element={<RequireSubscription><AvatarChatPage /></RequireSubscription>} />
        <Route path="/select-plan" element={<PlanSelectionPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/addon/success" element={<AddonPaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PlanSelectionPage />} />
        <Route path="/projects" element={<ProjectChatPage />} />
        <Route path="/legal" element={<PrivacyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/beta" element={<BetaSignupPage />} />
        <Route path="/beta-expired" element={<BetaExpiredPage />} />
        <Route path="/relation-capture" element={<RelationCaptureScreen />} />
        {/* Catch-all route - redirect to home if route not found */}
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </div>
  );
}

export default App;
